export type ResizeHandle = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

export interface PanelGeometry {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface PanelBounds {
  minWidth: number;
  maxWidth: number;
  minHeight: number;
  maxHeight: number;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, Math.round(value)));
}

export function clampPanelSize(geometry: PanelGeometry, bounds: PanelBounds): PanelGeometry {
  return {
    ...geometry,
    width: clamp(geometry.width, bounds.minWidth, bounds.maxWidth),
    height: clamp(geometry.height, bounds.minHeight, bounds.maxHeight),
  };
}

export function movePanelBy(geometry: PanelGeometry, dx: number, dy: number): PanelGeometry {
  return { ...geometry, left: Math.round(geometry.left + dx), top: Math.round(geometry.top + dy) };
}

/**
 * Resize one edge/corner while keeping the opposite edge fixed in the final
 * geometry. Owlbear can resize the outer iframe live, but it cannot move that
 * iframe live; callers commit the new left/top when the pointer is released.
 */
export function resizePanelFromHandle(
  geometry: PanelGeometry,
  handle: ResizeHandle,
  dx: number,
  dy: number,
  bounds: PanelBounds,
): PanelGeometry {
  const right = geometry.left + geometry.width;
  const bottom = geometry.top + geometry.height;
  let left = geometry.left;
  let top = geometry.top;
  let width = geometry.width;
  let height = geometry.height;

  if (handle.includes('e')) width = geometry.width + dx;
  if (handle.includes('s')) height = geometry.height + dy;
  if (handle.includes('w')) {
    width = geometry.width - dx;
    left = geometry.left + dx;
  }
  if (handle.includes('n')) {
    height = geometry.height - dy;
    top = geometry.top + dy;
  }

  width = clamp(width, bounds.minWidth, bounds.maxWidth);
  height = clamp(height, bounds.minHeight, bounds.maxHeight);

  // If clamping stopped a north/west edge, recompute its coordinate so the
  // far edge stays fixed instead of letting the panel creep.
  if (handle.includes('w')) left = right - width;
  if (handle.includes('n')) top = bottom - height;

  return { left: Math.round(left), top: Math.round(top), width, height };
}

/** Keep enough of a panel on-screen that its title bar can always be recovered. */
export function clampPanelPosition(
  geometry: PanelGeometry,
  viewportWidth: number,
  viewportHeight: number,
  visibleWidth = 120,
  visibleHeight = 44,
): PanelGeometry {
  const minLeft = -Math.max(0, geometry.width - visibleWidth);
  const maxLeft = Math.max(0, viewportWidth - visibleWidth);
  const minTop = 0;
  const maxTop = Math.max(0, viewportHeight - visibleHeight);
  return {
    ...geometry,
    left: clamp(geometry.left, minLeft, maxLeft),
    top: clamp(geometry.top, minTop, maxTop),
  };
}
